import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { download } from '@core/operators';
import { DataService, InvoiceRecord, InvoiceState, InvoiceType, SKU2Parts, DateRange, ProductUnit } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { TagsService } from '@core/services/tags.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { XLSX } from '@core/utilities/xlsx';
import { MessageBoxService } from '@routes/common/message-box.service';
import { Observable } from 'rxjs';

interface GoodGroup {
  SKU: string;
  Color: string;
  Count: number;
  Unit: ProductUnit;
  Quantities: { Quantity: number, Exported: boolean, Returned: boolean, Discount: number }[];
  ActualQuantity: number;
  ReducedQuantity: number;
  QuoteQuantity: number;
  Price: number;  
  Subtotal: number;
}

interface SourceGroup extends GoodGroup {
  ImportTag: string;
}

interface TargetGroup extends GoodGroup {
  ExportTag: string;
}

interface FlowGroup extends GoodGroup {
  ImportTag: string;
  ExportTag: string;
}

function sku(record: { SKU: string }): string {
  let parts = SKU2Parts(record.SKU);
  return (parts[0].length > 1 ? parts[0] + '-' : parts[0]) + parts[1];
}


@Component({
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss']
})
export class ViewerComponent implements OnInit {

  @Input()
  public filters: { customer?: string, vendor?: string, sku?: string, color?: string, importTag?: string, exportTag?: string, exportAfter?: { name: string, range: DateRange } };

  @Input()
  public invoice: InvoiceRecord;

  @Input()
  public payments: { Amount: number, Stamp: number, Tag: string }[] = [];

  @Output()
  public unconfirmed = new EventEmitter<InvoiceRecord>();  

  public get now(): Date {
    return new Date();
  }

  public filterName: string = '';  

  public goods = new Filterable<GoodGroup>({
    extractor: i => [i.SKU + 'm' + i.Color + "m"],
    grouper: i => [{ name: 'Mã hàng', option: sku(i) }, { name: 'Loại hàng', option: i.Unit == ProductUnit.Kilogram ? 'Hàng cân' : 'Hàng mét' }]
  });

  public sources = new Filterable<SourceGroup>({
    extractor: i => [i.SKU + 'm' + i.Color + "m"],
    grouper: i => [{ name: 'Nguồn nhập hàng', option: this.name(i.ImportTag).toUpperCase() }, { name: 'Mã hàng', option: sku(i) }, { name: 'Loại hàng', option: i.Unit == ProductUnit.Kilogram ? 'Hàng cân' : 'Hàng mét' }]
  });

  public targets = new Filterable<TargetGroup>({
    extractor: i => [i.SKU + 'm' + i.Color + "m"],
    grouper: i => [{ name: 'Khách mua hàng', option: this.name(i.ExportTag).toUpperCase() }, { name: 'Mã hàng', option: sku(i) }, { name: 'Loại hàng', option: i.Unit == ProductUnit.Kilogram ? 'Hàng cân' : 'Hàng mét' }]
  });

  public flows = new Filterable<FlowGroup>({
    extractor: i => [i.SKU + 'm' + i.Color + "m"],
    grouper: i => [{ name: 'Nguồn nhập hàng', option: this.name(i.ImportTag).toUpperCase() }, { name: 'Khách mua hàng', option: this.name(i.ExportTag).toUpperCase() }, { name: 'Mã hàng', option: sku(i) }, { name: 'Loại hàng', option: i.Unit == ProductUnit.Kilogram ? 'Hàng cân' : 'Hàng mét' }],
  });

  public filtered = new Filterable<FlowGroup>({
    extractor: i => [i.SKU + 'm' + i.Color + "m"],
    grouper: i => [{ name: 'Nguồn nhập hàng', option: this.name(i.ImportTag).toUpperCase() }, { name: 'Khách mua hàng', option: this.name(i.ExportTag).toUpperCase() }, { name: 'Mã hàng', option: sku(i) }, { name: 'Loại hàng', option: i.Unit == ProductUnit.Kilogram ? 'Hàng cân' : 'Hàng mét' }],
  })

  public details: any = this.goods;

  public get title(): string {

    let partner = this.partners.get(this.invoice.PartnerID);
    return Multilingual.getText('Đơn ' + (this.invoice.Type == InvoiceType.Import ? (this.invoice.PartnerID == 1 ? 'nhập hàng' : 'trả hàng') : this.invoice.PartnerID == 1 ? 'trừ hàng mất' : 'xuất hàng')) + ' ' + this.invoice.Tag + ' ' + Multilingual.getText('ngày') + ' ' + this.date.transform(this.invoice.Stamp, 'dd-MM-yyyy') + ' ' + Multilingual.getText(this.invoice.Type == InvoiceType.Import ? 'từ' : 'cho') + ' ' + (partner ? partner.Name : 'K' + this.invoice.PartnerID) + ' (' + this.invoice.ID + ')';
  }

  public get unconfirmable(): boolean {
    return this.users.current && this.users.current.Roles.contains("Owner") && this.invoice.State == InvoiceState.Confirmed && (this.invoice.Type == InvoiceType.Export || this.invoice.Details.count(i => !i.IsReturned && i.CustomerID > 0) < 1);
  }

  public get loading(): boolean {
    return !this.sync.ready(ViewerComponent);
  }

  public name(tag: string): string {
    let t = this.tags.get(tag);
    if (!t) return Multilingual.getText('không rõ');
    let p = this.partners.get(t.PartnerID);
    return p ? p.Name : Multilingual.getText('không rõ');;
  }

  public unconfirm() {

    this.mb.confirms('discourage', Multilingual.getText('Bạn muốn mở lại đơn hàng') + ': ' + this.invoice.Tag + '?', () => {

      return new Observable<boolean>(subscriber => {
        this.sync.with(ViewerComponent).commit(done => this.ds.invoices('unconfirm', { ID: this.invoice.ID }).subscribe(results => {

          if (results && results.length == 1) {

            this.invoice.State = results[0].State;
            this.unconfirmed.emit(results[0]);

            subscriber.next(true);
          }
          else {
            subscriber.next(false);
          }

          done();
        }));
      });
    });
  }

  constructor(private ds: DataService, private partners: PartnersService, private tags: TagsService, private sync: SynchronizationService, private users: UsersService, private mb: MessageBoxService, private date: DatePipe) { }

  ngOnInit(): void {

    this.sync.notify(ViewerComponent, false);

    this.sync.when(TagsService, PartnersService).commit(() => {      

      if (this.filters) {

        let details = this.invoice.Details;

        if (this.filters.sku) details = details.filter(i => i.SKU == this.filters.sku);
        if (this.filters.color) details = details.filter(i => i.Color == this.filters.color);
        if (this.filters.customer) details = details.filter(i => this.name(i.ExportInvoiceTag) == this.filters.customer);
        if (this.filters.vendor) details = details.filter(i => this.name(i.ImportInvoiceTag) == this.filters.vendor);
        if (this.filters.importTag) details = details.filter(i => i.ImportInvoiceTag == this.filters.importTag);
        if (this.filters.exportTag) details = details.filter(i => i.ExportInvoiceTag == (this.filters.exportTag == '' ? null : this.filters.exportTag));
        if (this.filters.exportAfter) details = details.filter(i => i.ExportInvoiceTag == null || this.tags.get(i.ExportInvoiceTag).Stamp > this.filters.exportAfter.range.to.getTime());

        this.filterName = [
          this.filters.sku ? Multilingual.getText("mã") + ' ' + sku({ SKU: this.filters.sku }) : Multilingual.getText('hàng'),
          this.filters.color ? Multilingual.getText("màu") + ' ' + this.filters.color : '',
          this.filters.exportAfter ? Multilingual.getText('xuất sau') + ' ' + this.filters.exportAfter.name : this.filters.importTag ? (this.invoice.Tag != this.filters.importTag ? Multilingual.getText('nhập từ') + ' ' + this.filters.importTag : '') : this.filters.vendor ? (this.partners.get(this.invoice.PartnerID).Name != this.filters.vendor ? Multilingual.getText('nhập từ') + ' ' + this.filters.vendor : '') : '',
          this.filters.exportTag ? (this.invoice.Tag != this.filters.exportTag ? Multilingual.getText('xuất tới') + ' ' + this.filters.exportTag : '') : this.filters.customer ? (this.partners.get(this.invoice.PartnerID).Name != this.filters.customer ? Multilingual.getText('xuất tới') + ' ' + this.filters.customer : '') : '',
        ].filter(i => i.length > 0).join(' ');


        if (details.length < this.invoice.Details.length) {

          this.filtered.source = details.group(i => [i.ImportInvoiceTag, i.ExportInvoiceTag, i.SKU, i.Color, i.QuoteUnitPrice, i.Unit].join('/')).map<FlowGroup>(i => ({
            ImportTag: i.items[0].ImportInvoiceTag,
            ExportTag: i.items[0].ExportInvoiceTag,
            SKU: i.items[0].SKU,
            Color: i.items[0].Color,
            Unit: i.items[0].Unit,
            Count: i.items.length,
            Quantities: i.items.map(i => ({ Quantity: i.ActualQuantity, Exported: i.CustomerID > 0, Returned: i.IsReturned, Discount: i.DiscountQuantity })),
            ActualQuantity: i.items.sum(i => i.ActualQuantity),
            ReducedQuantity: i.items.sum(i => i.ActualQuantity - i.QuoteQuantity),
            QuoteQuantity: i.items.sum(i => i.QuoteQuantity),
            Price: i.items[0].QuoteUnitPrice,
            Subtotal: i.items.sum(i => i.QuoteQuantity * i.QuoteUnitPrice)
          }));

          this.details = this.filtered;
        }
        else {
          this.filters = undefined;
          this.details = this.flows;
        }
      }
      else {
        this.details = this.goods;
      }


      this.goods.source = this.invoice.Details.group(i => [i.SKU, i.Color, i.QuoteUnitPrice, i.Unit].join('/')).map<GoodGroup>(i => ({
        SKU: i.items[0].SKU,
        Color: i.items[0].Color,
        Unit: i.items[0].Unit,
        Count: i.items.length,
        Quantities: i.items.map(i => ({ Quantity: i.ActualQuantity, Exported: i.CustomerID > 0, Returned: i.IsReturned, Discount: i.DiscountQuantity })),
        ActualQuantity: i.items.sum(i => i.ActualQuantity),
        ReducedQuantity: i.items.sum(i => i.ActualQuantity - i.QuoteQuantity),
        QuoteQuantity: i.items.sum(i => i.QuoteQuantity),
        Price: i.items[0].QuoteUnitPrice,
        Subtotal: i.items.sum(i => i.QuoteQuantity * i.QuoteUnitPrice)
      }));

      this.sources.source = this.invoice.Details.group(i => [i.ImportInvoiceTag, i.SKU, i.Color, i.QuoteUnitPrice, i.Unit].join('/')).map<SourceGroup>(i => ({
        ImportTag: i.items[0].ImportInvoiceTag,
        SKU: i.items[0].SKU,
        Color: i.items[0].Color,
        Unit: i.items[0].Unit,
        Count: i.items.length,
        Quantities: i.items.map(i => ({ Quantity: i.ActualQuantity, Exported: i.CustomerID > 0, Returned: i.IsReturned, Discount: i.DiscountQuantity })),
        ActualQuantity: i.items.sum(i => i.ActualQuantity),
        ReducedQuantity: i.items.sum(i => i.ActualQuantity - i.QuoteQuantity),
        QuoteQuantity: i.items.sum(i => i.QuoteQuantity),
        Price: i.items[0].QuoteUnitPrice,
        Subtotal: i.items.sum(i => i.QuoteQuantity * i.QuoteUnitPrice)
      }));

      this.targets.source = this.invoice.Details.group(i => [i.ExportInvoiceTag, i.SKU, i.Color, i.QuoteUnitPrice, i.Unit].join('/')).map<TargetGroup>(i => ({
        ExportTag: i.items[0].ExportInvoiceTag,
        SKU: i.items[0].SKU,
        Color: i.items[0].Color,
        Unit: i.items[0].Unit,
        Count: i.items.length,
        Quantities: i.items.map(i => ({ Quantity: i.ActualQuantity, Exported: i.CustomerID > 0, Returned: i.IsReturned, Discount: i.DiscountQuantity })),
        ActualQuantity: i.items.sum(i => i.ActualQuantity),
        ReducedQuantity: i.items.sum(i => i.ActualQuantity - i.QuoteQuantity),
        QuoteQuantity: i.items.sum(i => i.QuoteQuantity),
        Price: i.items[0].QuoteUnitPrice,
        Subtotal: i.items.sum(i => i.QuoteQuantity * i.QuoteUnitPrice)
      }));

      this.flows.source = this.invoice.Details.group(i => [i.ImportInvoiceTag, i.ExportInvoiceTag, i.SKU, i.Color, i.QuoteUnitPrice, i.Unit].join('/')).map<FlowGroup>(i => ({
        ImportTag: i.items[0].ImportInvoiceTag,
        ExportTag: i.items[0].ExportInvoiceTag,
        SKU: i.items[0].SKU,
        Color: i.items[0].Color,
        Unit: i.items[0].Unit,
        Count: i.items.length,
        Quantities: i.items.map(i => ({ Quantity: i.ActualQuantity, Exported: i.CustomerID > 0, Returned: i.IsReturned, Discount: i.DiscountQuantity })),
        ActualQuantity: i.items.sum(i => i.ActualQuantity),
        ReducedQuantity: i.items.sum(i => i.ActualQuantity - i.QuoteQuantity),
        QuoteQuantity: i.items.sum(i => i.QuoteQuantity),
        Price: i.items[0].QuoteUnitPrice,
        Subtotal: i.items.sum(i => i.QuoteQuantity * i.QuoteUnitPrice)
      }));      

      this.sync.notify(ViewerComponent, true);
    });
  }

  public exportExcel() {

    this.sync.with(ViewerComponent).commit(done => {

      let xlsx = new XLSX();

      let sheet = xlsx.worksheet(this.invoice.Tag + ' (' + this.invoice.ID + ')');

      let rows = this.generateExportableData(this.flows, "source", true);

      for (let y = 0; y < rows.length; y++) {
        sheet.row(y + 1, ...rows[y]);        
      }

      xlsx.decimalFormat = '##,0.0';

      ['left', 'left', 'center', 'center', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'center', 'center', 'center', 'center'].forEach((a, i) => sheet.column(i + 1).align = a as any);

      for (let i = 1; i < 5; i++) sheet.column(i).valign = "top";
      for (let i = 11; i < 20; i++) sheet.column(i).valign = "bottom";

      sheet.column(11).number = 'decimal';
      sheet.column(12).number = this.invoice.PartnerID == 1 ? 'decimal' : 'integer';      

      for (let y = 2; y <= sheet.count('rows'); y++) {

        ["A", "B", "C", "D"].commit(c => {
          if (sheet.has(c, y)) sheet.cell(c, y).merge('down');
        });        
      }

      for (let y = sheet.count('rows'); y > 1; y--) {
        ["K", "L", "M", "N", "O", "P", "Q", "R"].commit(c => {
          if (sheet.has(c, y)) sheet.cell(c, y).merge('up');
        });
      }

      sheet.frozenTopRowCount = 1;

      xlsx.generate().subscribe(base64 => {
        download(this.title + ".xlsx", base64);
        done();
      });    
    });

  }

  public copy() {

    try {
      navigator.clipboard.writeText(this.generateExportableData(this.details, "matches", false).map(rows => rows.join('\t')).join('\n'));
      this.mb.informs('<b class="text-success uppercase">' + Multilingual.getText('Dữ liệu đơn hàng đã được sao chép thành công') + '</b>');
    }
    catch (ex) {
      this.mb.informs(Multilingual.getText('<b class="text-danger uppercase">' + 'Không sao chép được dữ liệu đơn hàng' + '</b>'));
    }
  }

  public print() {
    window.print();
  }

  private generateExportableData(source: any, mode: "matches" | "source", header: boolean): any[][] {

    let rows: any[][] = [];

    if (header) {

      let headers = ['Mã đơn', 'Mã hàng', 'Màu', 'Số lượng', 'Cây 1', 'Cây 2', 'Cây 3', 'Cây 4', 'Cây 5', 'Cây 6', 'Tổng dài', 'Khấu trừ', 'Tính tiền', 'Giá', 'Thành tiền'];

      /*
      if (source == this.flows || source == this.sources || source == this.filtered) {
        headers.push('Nguồn', 'Đơn nhập');
      }

      if (source == this.flows || source == this.targets || source == this.filtered) {
        headers.push('Khách', 'Đơn xuất');
      }
      */

      rows.push(headers.map(i => Multilingual.getText(i).toUpperCase()))
    }

    rows.push([this.invoice.Tag]);

    (mode == 'matches' ? source.matches : source.source).forEach(group => {

      rows.last().push(group.SKU, group.Color, group.Quantities.length);

      for (let i = 0; i < group.Quantities.length; i++) {

        if (i > 0 && i % 6 == 0) {

          rows.push([null, null, null, null]);
        }

        rows.last().push(group.Quantities[i].Quantity);
      }

      while (rows.last().length < 10) rows.last().push(null);

      rows.last().push(group.ActualQuantity, group.ReducedQuantity, group.QuoteQuantity, group.Price, group.Subtotal);

      /*
      if (source == this.flows || source == this.sources || source == this.filtered) {
        rows.last().push(this.name(group.ImportTag), group.ImportTag);
      }

      if (source == this.flows || source == this.targets || source == this.filtered) {
        rows.last().push(this.name(group.ExportTag), group.ExportTag);
      }
      */

      rows.push([null]);
    });

    rows.pop();

    return rows;
  }

  public extractor(field: string): SortableExtractor<any> {
    return {
      extract: i => {
        switch (field) {
          case 'Vendor': return this.name(i.ImportTag);
          case 'Customer': return this.name(i.ExportTag);
          default: return i[field] ? i[field] : ''
        }        
      }
    };
  }

  public sum(field: string, unit?: ProductUnit): number {
    return this.details.matches.sum(i => !unit || unit == i.Unit ? i[field] : 0);
  }

  public get paid(): number {
    return this.payments.sum(i => i.Amount);
  }


}
